// SEO (per-route <head> management) + analytics helpers for public marketing routes.
// React 19 doesn't need react-helmet — we manage the document head imperatively so it
// stays reliable, dedupes cleanly, and coexists with the static index.html.
import { useEffect } from 'react';

export const BIZ = {
  name: 'The T Touch Cleaning Services',
  phone: '+61466955793',
  phoneDisplay: '0466 955 793',
  abn: '30 984 107 905',
  url: 'https://www.thettouch.com.au',
  email: 'taleeia@thettouch.com.au',
  region: 'Moreton Bay',
  areas: ['Eatons Hill', 'Albany Creek', 'Cashmere', 'Warner', 'Strathpine', 'Bray Park', 'Petrie', 'Kallangur'],
};

function upsertMeta(attr, key, content) {
  if (!content) return;
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) { el = document.createElement('meta'); el.setAttribute(attr, key); document.head.appendChild(el); }
  el.setAttribute('content', content);
}

function upsertLink(rel, href) {
  if (!href) return;
  let el = document.head.querySelector(`link[rel="${rel}"]`);
  if (!el) { el = document.createElement('link'); el.setAttribute('rel', rel); document.head.appendChild(el); }
  el.setAttribute('href', href);
}

export function useSeo({ title, description, canonical, image, jsonLd, noindex }) {
  const jsonLdKey = jsonLd ? JSON.stringify(jsonLd) : '';
  useEffect(() => {
    if (title) document.title = title;  upsertMeta('name', 'description', description);
    upsertMeta('name', 'robots', noindex ? 'noindex,nofollow' : 'index,follow');
    upsertMeta('property', 'og:site_name', BIZ.name);
    upsertMeta('property', 'og:title', title);
    upsertMeta('property', 'og:description', description);
    upsertMeta('property', 'og:type', 'website');
    upsertMeta('property', 'og:locale', 'en_AU');
    if (canonical) upsertMeta('property', 'og:url', canonical);
    if (image) upsertMeta('property', 'og:image', image);
    upsertMeta('name', 'twitter:card', image ? 'summary_large_image' : 'summary');
    upsertMeta('name', 'twitter:title', title);
    upsertMeta('name', 'twitter:description', description);
    if (image) upsertMeta('name', 'twitter:image', image);
    if (canonical) upsertLink('canonical', canonical);

    document.head.querySelectorAll('script[data-seo-jsonld]').forEach((n) => n.remove());
    if (jsonLd) {
      (Array.isArray(jsonLd) ? jsonLd : [jsonLd]).forEach((obj) => {
        const s = document.createElement('script');
        s.type = 'application/ld+json';
        s.setAttribute('data-seo-jsonld', '1');
        s.text = JSON.stringify(obj);
        document.head.appendChild(s);
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title, description, canonical, image, noindex, jsonLdKey]);
}

export function localBusinessLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'HouseCleaningService',
    '@id': `${BIZ.url}/#business`,
    name: BIZ.name,
    url: BIZ.url,
    telephone: BIZ.phone,
    email: BIZ.email,
    image: `${BIZ.url}/assets/logo.png`,
    logo: `${BIZ.url}/assets/logo.png`,
    priceRange: '$$',
    identifier: { '@type': 'PropertyValue', propertyID: 'ABN', value: BIZ.abn },
    address: { '@type': 'PostalAddress', addressRegion: 'QLD', addressCountry: 'AU', addressLocality: BIZ.region },
    areaServed: BIZ.areas.map((a) => ({ '@type': 'City', name: a })),
    openingHoursSpecification: [{
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '07:00', closes: '17:00',
    }],
  };
}

export function faqLd(pairs) {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: (pairs || []).map(([q, a]) => ({
      '@type': 'Question',
      name: q,
      acceptedAnswer: { '@type': 'Answer', text: a },
    })),
  };
}

// ---- Analytics: env-configurable, fully dormant/inert when the IDs are empty ----
const GA4 = process.env.REACT_APP_GA4_ID;
const PIXEL = process.env.REACT_APP_META_PIXEL_ID;

export function initAnalytics() {
  if (typeof window === 'undefined') return;
  // Google Search Console verification (meta-tag method) — dormant unless the env slot is set.
  const gsc = process.env.REACT_APP_GSC_VERIFICATION;
  if (gsc && !document.head.querySelector('meta[name="google-site-verification"]')) {
    upsertMeta('name', 'google-site-verification', gsc);
  }
  if (GA4 && !window.__ttGaInit) {
    window.__ttGaInit = true;
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtag/js?id=${GA4}`;
    document.head.appendChild(s);
    window.dataLayer = window.dataLayer || [];
    window.gtag = function () { window.dataLayer.push(arguments); };
    window.gtag('js', new Date());
    window.gtag('config', GA4);
  }
  if (PIXEL && !window.__ttFbInit) {
    window.__ttFbInit = true;
    /* eslint-disable */
    !function (f, b, e, v, n, t, s) {
      if (f.fbq) return; n = f.fbq = function () { n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments); };
      if (!f._fbq) f._fbq = n; n.push = n; n.loaded = !0; n.version = '2.0'; n.queue = [];
      t = b.createElement(e); t.async = !0; t.src = v; s = b.getElementsByTagName(e)[0]; s.parentNode.insertBefore(t, s);
    }(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    /* eslint-enable */
    window.fbq('init', PIXEL);
    window.fbq('track', 'PageView');
  }
}

const FB_MAP = { generate_lead: 'Lead', contact: 'Contact' };

export function trackEvent(name, params = {}) {
  try { if (window.gtag) window.gtag('event', name, params); } catch (e) { /* noop */ }
  try { if (window.fbq) window.fbq('track', FB_MAP[name] || 'CustomEvent', params); } catch (e) { /* noop */ }
}
